import React from 'react';
import { AccessToken } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import { CheckCircle } from '@material-ui/icons';
import { AnalyticsEvents } from '../../../../components/analytics/analyticsEvents';
import { useLegacyTelematicsDeviceContext } from '../../stores/LegacyTelematicsDevice/LegacyTelematicsDeviceProvider';
import { useDeviceContext } from '../../stores/TelematicsDevice/TelematicsDeviceProvider';
import GenericConfigButton from '../GenericConfiguration/GenericConfigButton';
import {
  ConfigurationTemplate,
  TelematicsDeviceConfigurationV2Api
} from '../../stores/TelematicsDeviceConfiguration/TelematicsDeviceConfigurationV2Api';
import { DeviceStateApi } from '../../stores/TelematicsDevice/DeviceStateApi';
import { getCustomConfigurationStatus } from './CustomConfigurationStatus';
import '../DeviceState/StateStatus.scss';

const CONFIGURED_COLOR = 'rgb(21, 128, 61)';

export interface CustomConfigurationProps {
  configurationKey: string;
  configurationName: string;
  analyticsEvent: AnalyticsEvents;
  label?: string;
  additionalSetup?: (accessToken: AccessToken | undefined, serialNumber: string) => Promise<unknown>;
}

export const CustomConfiguration: React.FC<CustomConfigurationProps> = props => {
  const { authState } = useOktaAuth();
  const telematicsDevice = useLegacyTelematicsDeviceContext().telematicsDevice;
  const { identity, state, reloadState } = useDeviceContext();
  const [configurationTemplate, setConfigurationTemplate] = React.useState<ConfigurationTemplate | null>(null);
  const status = getCustomConfigurationStatus(state, props.configurationKey);

  React.useEffect(() => {
    if (telematicsDevice) {
      TelematicsDeviceConfigurationV2Api.getDeviceConfigurationTemplates(
        authState?.accessToken,
        telematicsDevice.serialNumber
      ).then((configurationTemplates: ConfigurationTemplate[]) => {
        const matchingTemplates = configurationTemplates?.filter(configurationTemplate =>
          configurationTemplate.href.includes(props.configurationName)
        );
        setConfigurationTemplate(matchingTemplates?.length === 1 ? matchingTemplates[0] : null);
      });
    } else {
      setConfigurationTemplate(null);
    }
  }, [telematicsDevice]);

  const getLabel = () => props.label ?? configurationTemplate?.name ?? '';

  const getStatusIndicator = () => {
    if (status.configured) {
      return <CheckCircle titleAccess="configured" htmlColor={CONFIGURED_COLOR} />;
    }

    if (status.pending) {
      return (
        <div className="state">
          <div className="pending"><div>pending</div></div>
        </div>
      );
    }

    return undefined;
  };

  const performConfiguration = () => {
    if (!!configurationTemplate && !!telematicsDevice && !!identity) {
      const setupActions: Promise<unknown>[] = [
        DeviceStateApi.updateState(
          authState?.accessToken,
          identity.id,
          { customConfigurations: { [props.configurationKey]: {} } }
        ),
      ];

      if (props.additionalSetup) {
        setupActions.push(props.additionalSetup(authState?.accessToken, telematicsDevice.serialNumber));
      }

      return Promise.all(setupActions)
        .then(() => reloadState())
        .then(() => undefined)
        .catch(() => Promise.reject(new Error(`${getLabel()} configuration failed.`)));
    }

    return Promise.reject(new Error('Missing required data to perform device setup.'));
  };

  return (
    <GenericConfigButton
      visible={!!configurationTemplate}
      disabled={status.configured || status.pending}
      endIcon={getStatusIndicator()}
      buttonLabel={getLabel()}
      confirmLabel={`Are you sure you want to execute configuration: ${getLabel()}?`}
      analyticsEvent={props.analyticsEvent}
      performConfiguration={() => performConfiguration()}
    />
  );
};

export default CustomConfiguration;
